import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import {
  useAppNavigate,
  usePaneLocation,
  usePaneParams,
} from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import type {
  GithubIssueEntity,
  GithubProjectItemEntity,
} from "@cohesive/api-entities";
import {
  type Entity,
  IntegrationIcon,
  Markdown,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import { MessageSquare, Target, X, XCircle } from "lucide-react";
import { useCallback } from "react";

export function GitHubIssueDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  usePaneParams(ROUTES.GITHUB.ISSUE_DETAIL);
  const paneLocation = usePaneLocation("rightPane");
  const entityFromState = paneLocation?.state?.entity as
    | GithubIssueEntity
    | GithubProjectItemEntity
    | undefined;

  const rawData = paneLocation?.state?.rawData as any;

  const issue = entityFromState?.data
    ? entityFromState.type === "github_project_item"
      ? {
          id: entityFromState.data.id,
          web_link:
            entityFromState.data.content_url || entityFromState.data.web_link,
          title: entityFromState.data.content_title,
          summary: entityFromState.data.content_title,
          issue_number: entityFromState.data.content_number,
          state: entityFromState.data.content_state?.toLowerCase(),
          owner: entityFromState.data.repository_owner,
          repo: entityFromState.data.repository_name,
          description: entityFromState.data.content_body,
          created_at: entityFromState.data.created_at,
          updated_at: entityFromState.data.updated_at,
          author: "Unknown",
          author_id: "unknown",
          author_avatar_url: undefined,
          assignees: [],
          labels: [],
          milestone: undefined,
          milestone_title: undefined,
          comments_count: 0,
          closed_at: undefined,
        }
      : {
          ...entityFromState.data,
          author_avatar_url: rawData?.user?.avatar_url,
          assignees: rawData?.assignees || entityFromState.data.assignees,
          labels: rawData?.labels || entityFromState.data.github_labels || [],
          milestone: rawData?.milestone,
          milestone_title: entityFromState.data.milestone_title,
        }
    : undefined;

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  if (!issue) {
    return (
      <div className="flex flex-col h-full bg-background items-center justify-center">
        <XCircle className="h-10 w-10 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground">Issue not found</p>
      </div>
    );
  }

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />
          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <IntegrationIcon
              platform="github"
              name="GitHub"
              className="h-4 w-4 shrink-0"
            />
            <span className="text-sm font-medium text-foreground truncate leading-none">
              {issue.title}
            </span>
          </div>
          <TooltipProvider>
            {entityFromState && (
              <EntityPaneHeaderObjectActions
                entity={entityFromState as Entity}
              />
            )}
            <PaneExpandButton />
            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent>
        <div className="space-y-5">
          {/* Author section */}
          <div className="flex items-start gap-3">
            <Avatar className="h-8 w-8">
              {issue.author_avatar_url && (
                <AvatarImage src={issue.author_avatar_url} alt={issue.author} />
              )}
              <AvatarFallback className="text-sm">
                {issue.author?.charAt(0).toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap text-sm">
                <span className="font-semibold">{issue.author}</span>
                <span className="text-muted-foreground">opened this issue</span>
                {issue.created_at && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground">
                          {formatDistanceToNow(new Date(issue.created_at), {
                            addSuffix: true,
                          })}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {new Date(issue.created_at).toLocaleString()}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {issue.updated_at && issue.updated_at !== issue.created_at && (
                <div className="text-xs text-muted-foreground mt-1">
                  Updated{" "}
                  {formatDistanceToNow(new Date(issue.updated_at), {
                    addSuffix: true,
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Metadata section */}
          <div className="space-y-3 pb-4 border-b">
            {/* Assignees */}
            {issue.assignees && issue.assignees.length > 0 && (
              <div className="flex items-start gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0">
                  Assignees
                </span>
                <div className="flex flex-wrap gap-2">
                  {issue.assignees.map((assignee: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Avatar className="h-5 w-5">
                        {assignee.avatar_url && (
                          <AvatarImage
                            src={assignee.avatar_url}
                            alt={assignee.login}
                          />
                        )}
                        <AvatarFallback className="text-[10px]">
                          {assignee.login?.charAt(0).toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <span>{assignee.login}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Labels */}
            {issue.labels && issue.labels.length > 0 && (
              <div className="flex items-start gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0">
                  Labels
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {issue.labels.map((label: any, i: number) => (
                    <TooltipProvider key={i}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="outline"
                            className="text-xs"
                            style={{
                              borderColor: label.color
                                ? `#${label.color}`
                                : undefined,
                              backgroundColor: label.color
                                ? `#${label.color}20`
                                : undefined,
                              color: label.color
                                ? `#${label.color}`
                                : undefined,
                            }}
                          >
                            {label.name}
                          </Badge>
                        </TooltipTrigger>
                        {label.description && (
                          <TooltipContent className="max-w-xs">
                            {label.description}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            )}

            {/* Milestone */}
            {(issue.milestone?.title || issue.milestone_title) && (
              <div className="flex items-start gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0 flex items-center gap-1.5">
                  <Target className="h-3.5 w-3.5" />
                  Milestone
                </span>
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium">
                    {issue.milestone?.title || issue.milestone_title}
                  </p>
                  {issue.milestone?.open_issues !== undefined &&
                    issue.milestone?.closed_issues !== undefined && (
                      <div className="flex items-center gap-2">
                        <div className="h-2 flex-1 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-lc-active"
                            style={{
                              width: `${
                                (issue.milestone.closed_issues /
                                  (issue.milestone.open_issues +
                                    issue.milestone.closed_issues)) *
                                100
                              }%`,
                            }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {issue.milestone.closed_issues} /{" "}
                          {issue.milestone.open_issues +
                            issue.milestone.closed_issues}
                        </span>
                      </div>
                    )}
                  {issue.milestone?.due_on && (
                    <p className="text-xs text-muted-foreground">
                      Due{" "}
                      {formatDistanceToNow(new Date(issue.milestone.due_on), {
                        addSuffix: true,
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Comments */}
            {issue.comments_count !== undefined && issue.comments_count > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0 flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Comments
                </span>
                <span className="text-sm">{issue.comments_count}</span>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="prose prose-sm max-w-none dark:prose-invert">
            {issue.description ? (
              <Markdown>{issue.description}</Markdown>
            ) : (
              <p className="text-muted-foreground italic">
                No description provided
              </p>
            )}
          </div>
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
